/**
 * NotationContext.js
 *
 * Context React pour partager la nomenclature de batterie dans toute l'application.
 * Charge la nomenclature depuis Firebase au démarrage et la met à jour en temps réel.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import UserService from '../services/UserService';

const NotationContext = createContext();

export const useNotation = () => {
  const context = useContext(NotationContext);
  if (!context) {
    throw new Error('useNotation doit être utilisé dans un NotationProvider');
  }
  return context;
};

export const NotationProvider = ({ children }) => {
  const { currentUser, getEffectiveUserId } = useAuth();
  const [notation, setNotation] = useState('europe'); // Valeur par défaut
  const [isLoading, setIsLoading] = useState(true);

  // Charger la nomenclature depuis Firebase au démarrage
  useEffect(() => {
    const loadNotation = async () => {
      const userId = getEffectiveUserId();
      if (userId) {
        try {
          const userNotation = await UserService.getNotation(userId);
          setNotation(userNotation);
        } catch (error) {
          console.error('❌ Erreur chargement nomenclature:', error);
          setNotation('europe'); // Fallback
        } finally {
          setIsLoading(false);
        }
      } else {
        // Si pas de user, utiliser la valeur par défaut
        setNotation('europe');
        setIsLoading(false);
      }
    };

    loadNotation();
  }, [getEffectiveUserId, currentUser]);

  // Fonction pour mettre à jour la nomenclature
  const updateNotation = async (newNotation) => {
    const userId = getEffectiveUserId();
    if (userId) {
      try {
        await UserService.updateNotation(userId, newNotation);
        setNotation(newNotation);
        console.log('✅ Nomenclature mise à jour:', newNotation);
      } catch (error) {
        console.error('❌ Erreur mise à jour nomenclature:', error);
        throw error;
      }
    } else {
      // Si pas de user connecté, juste mettre à jour localement
      setNotation(newNotation);
    }
  };

  const value = {
    notation,
    updateNotation,
    isLoading
  };

  return (
    <NotationContext.Provider value={value}>
      {children}
    </NotationContext.Provider>
  );
};

export default NotationContext;
